import React, { useState, useEffect, useMemo } from "react";
import { X, ChevronLeft, ChevronRight, Loader2, Search, FileSpreadsheet } from "lucide-react";
import { format } from "date-fns";
import api, { baseURL } from "~/services/api";
import { toast } from "react-toastify";
import { saveAs } from "file-saver";

const USDollar = new Intl.NumberFormat("en-US", {
  style: "currency",
  currency: "USD",
});

const DEBOUNCE_MS = 450;

export default function CashflowModal({ isOpen, onClose, modalData }) {
  const [currentPage, setCurrentPage] = useState(1);
  const [isMobile, setIsMobile] = useState(typeof window !== "undefined" ? window.innerWidth < 768 : false);
  const [loading, setLoading] = useState(false);
  const [exportingExcel, setExportingExcel] = useState(false);
  const [error, setError] = useState(null);
  const [transactionData, setTransactionData] = useState(null);
  const [searchTerm, setSearchTerm] = useState("");
  const [debouncedTerm, setDebouncedTerm] = useState("");
  const [showDiscounts, setShowDiscounts] = useState(false);
  const limit = 10;

  useEffect(() => {
    const handleResize = () => setIsMobile(window.innerWidth < 768);
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  useEffect(() => {
    const t = setTimeout(() => setDebouncedTerm((searchTerm || "").trim()), DEBOUNCE_MS);
    return () => clearTimeout(t);
  }, [searchTerm]);

  useEffect(() => {
    if (isOpen) {
      setCurrentPage(1);
      setSearchTerm("");
      setDebouncedTerm("");
      setShowDiscounts(false);
      fetchTransactions();
    } else {
      setTransactionData(null);
      setError(null);
      setLoading(false);
      setShowDiscounts(false);
    }
  }, [isOpen, modalData?.accountLabel, modalData?.period]);

  useEffect(() => {
    setCurrentPage(1);
  }, [debouncedTerm]);

  useEffect(() => {
    if (isOpen && (modalData?.type === "deduction" || modalData?.type === "discounts_revenue")) {
      fetchTransactions();
    }
  }, [showDiscounts]);

  const buildQueryParams = () => {
    if (!modalData?.period) return null;

    const codeMatch = modalData?.accountLabel?.match(/^([\d.]+)\s/);
    const accountCode = codeMatch ? codeMatch[1] : null;
    if (accountCode) {
      const params = { period: modalData.period, code: accountCode };
      if (modalData?.dateType) params.date_type = modalData.dateType;
      return params;
    }


    if (modalData?.type && modalData?.cashflowConfigId) {
      const params = { period: modalData.period, cashflow_config_id: modalData.cashflowConfigId };
      if (modalData.type === "deduction") {
        params.type = showDiscounts ? "deductions_discounts" : "deduction";
      } else if (modalData.type === "discounts_revenue") {
        params.type = "discounts_revenue";
      } else {
        params.type = modalData.type;
      }
      if (modalData?.dateType) params.date_type = modalData.dateType;
      return params;
    }


    if (modalData?.cashflowConfigId) {
      const params = { period: modalData.period, cashflow_config_id: modalData.cashflowConfigId };
      if (modalData?.dateType) params.date_type = modalData.dateType;
      return params;
    }

    return null;
  };

  const fetchTransactions = async () => {
    if (!modalData?.accountLabel && !modalData?.type) return;
    const params = buildQueryParams();
    if (!params || params === "NO_CODE") {
      setError("No account code found in label");
      return;
    }
    setLoading(true);
    setError(null);
    setTransactionData(null);
    try {
      const response = await api.get("/cashflowconfigs/transactions-by-account", { params });
      setTransactionData(response.data);
      setError(null);
    } catch (err) {
      setError("Failed to load transaction details");
      setTransactionData(null);
    } finally {
      setLoading(false);
    }
  };

  const handleExportExcel = async () => {
    const params = buildQueryParams();
    if (!params || params === "NO_CODE") {
      toast("parametros inválidos para exportação", { type: "error", autoClose: 2500 });
      return;
    }
    setExportingExcel(true);
    try {
      const { data } = await api.get("/cashflowconfigs/transactions-cashflow-excel", { params });
      const name = data?.name || `cashflow-transactions-${params.period}.xlsx`;
      saveAs(`${baseURL}/get-file/${name}`, name);
      toast("Excel gerado", { type: "success", autoClose: 2500 });
    } catch (err) {
      toast(err?.response?.data?.error || "Falha ao gerar Excel", { type: "error", autoClose: 3000 });
    } finally {
      setExportingExcel(false);
    }
  };

  const handlePageChange = (newPage) => {
    setCurrentPage(newPage);
  };

  const handleSearchInputChange = (e) => {
    setSearchTerm(e.target.value);
  };

  const handleClearSearch = () => {
    setSearchTerm("");
  };

  const normalized = (s) =>
    (s || "")
      .toString()
      .toLowerCase()
      .normalize("NFD")
      .replace(/[\u0300-\u036f]/g, "");

  const clientFilteredRows = useMemo(() => {
    const transactions = transactionData?.transactions || [];
    const term = normalized(debouncedTerm);
    if (!term) return transactions;
    return transactions.filter((row) => {
      const invoice = normalized(row.invoice_number);
      const branch = normalized(row.filial_name);
      const chartCode = normalized(row.chart_code);
      const chartName = normalized(row.chart_name);
      const memo = normalized(row.memo);
      return invoice.includes(term) || branch.includes(term) || chartCode.includes(term) || chartName.includes(term) || memo.includes(term);
    });
  }, [transactionData?.transactions, debouncedTerm]);

  const totalRowsUI = clientFilteredRows.length;
  const totalPages = totalRowsUI ? Math.ceil(totalRowsUI / limit) : 1;

  const startRecord = totalRowsUI ? (currentPage - 1) * limit + 1 : 0;
  const endRecord = totalRowsUI ? Math.min(currentPage * limit, totalRowsUI) : 0;

  const paginatedTransactions = useMemo(() => {
    const start = (currentPage - 1) * limit;
    return clientFilteredRows.slice(start, start + limit);
  }, [clientFilteredRows, currentPage]);

  const formatDate = (dateString) => {
    if (!dateString) return "-";
    try {
      if (dateString.includes("-") && dateString.length === 10) {
        const [year, month, day] = dateString.split("-");
        return `${month}/${day}/${year}`;
      }
      return format(new Date(dateString), "MM/dd/yyyy");
    } catch {
      return dateString;
    }
  };

  if (!isOpen) return null;

  

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50 p-4">
      <div className={`bg-white rounded-lg shadow-xl ${isMobile ? "w-full h-full" : "w-full max-w-7xl max-h-[90vh]"} flex flex-col`}>
        <div className="flex items-center justify-between p-4 border-b">
          <div className="flex-1">
            <h2 className={`${isMobile ? "text-lg" : "text-xl"} font-bold`}>Cashflow Details - {modalData?.accountLabel || "Account"}</h2>
            <p className="text-sm text-gray-600 mt-1">
              Period: {modalData?.period || "-"} • Value: {modalData?.value ? USDollar.format(modalData.value) : "-"}
            </p>
            {transactionData?.chart_of_account && (
              <div className="mt-2 text-xs text-gray-500">
                <span className="font-semibold">Code:</span> {transactionData.chart_of_account.code} •
                <span className="font-semibold ml-2">Name:</span> {transactionData.chart_of_account.name}
              </div>
            )}
            {transactionData?.summary && (
              <div className={`mt-2 grid ${(showDiscounts || modalData?.type === "code_discount" || modalData?.type === "discounts_revenue") ? "grid-cols-2 sm:grid-cols-4" : "grid-cols-2 sm:grid-cols-3"} gap-2 text-xs`}>
                <div className="bg-blue-50 p-2 rounded-sm">
                  <div className="text-gray-600">Total Amount</div>
                  <div className="font-semibold">{USDollar.format(transactionData.summary.total_amount || 0)}</div>
                </div>
                <div className="bg-yellow-50 p-2 rounded-sm">
                  <div className="text-gray-600">Total Fee</div>
                  <div className="font-semibold">{USDollar.format(transactionData.summary.total_fee || 0)}</div>
                </div>
                {(showDiscounts || modalData?.type === "code_discount" || modalData?.type === "discounts_revenue") && transactionData.summary.total_discount != null && (
                  <div className="bg-red-50 p-2 rounded-sm">
                    <div className="text-gray-600">Total Discount</div>
                    <div className="font-semibold">{USDollar.format(Math.round((transactionData.summary.total_discount || 0) * 100) / 100)}</div>
                  </div>
                )}
                <div className="bg-purple-50 p-2 rounded-sm">
                  <div className="text-gray-600">Count</div>
                  <div className="font-semibold">{transactionData.summary.count || 0}</div>
                </div>
              </div>
            )}
            {transactionData?.children_accounts && transactionData.children_accounts.length > 0 && (
              <div className="mt-2 text-xs text-gray-600">
                <span className="font-semibold">Child Accounts:</span> {transactionData.children_accounts.map((c) => c.code).join(", ")}
              </div>
            )}
          </div>
          <button onClick={onClose} className="p-2 hover:bg-gray-100 rounded-lg transition-colors">
            <X size={24} />
          </button>
        </div>

        <div className="p-4 border-b bg-gray-50">
          <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3">
            <div className="flex items-center gap-3 text-sm text-gray-700">
              {totalRowsUI > 0 && (
                <>
                  <span>
                    Showing <span className="font-semibold">{startRecord}</span> to <span className="font-semibold">{endRecord}</span> of <span className="font-semibold">{totalRowsUI}</span> rows
                  </span>
                  <span className="text-gray-400">|</span>
                  <span>Page</span>
                  <select
                    value={currentPage}
                    onChange={(e) => handlePageChange(Number(e.target.value))}
                    className="border border-gray-300 rounded-sm px-2 py-1 text-sm focus:outline-hidden focus:ring-2 focus:ring-orange-500"
                    disabled={loading || totalPages <= 1}
                  >
                    {Array.from({ length: totalPages || 1 }, (_, i) => i + 1).map((page) => (
                      <option key={page} value={page}>
                        {page}
                      </option>
                    ))}
                  </select>
                  <span>of {totalPages}</span>
                </>
              )}
            </div>

            <div className="flex items-center gap-3 w-full sm:w-auto">
              <div className="relative flex-1 sm:flex-initial">
                <div className="relative">
                  <input
                    type="text"
                    placeholder="Search by invoice, branch, chart..."
                    value={searchTerm}
                    onChange={handleSearchInputChange}
                    className="w-full sm:w-80 pl-10 pr-10 py-2 border border-gray-300 rounded-lg text-sm focus:outline-hidden focus:ring-2 focus:ring-orange-500"
                  />
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" size={18} />
                  {searchTerm && (
                    <button onClick={handleClearSearch} className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600">
                      <X size={18} />
                    </button>
                  )}
                </div>
              </div>

              {modalData?.type === "deduction" && (
                <label className="flex items-center gap-2 cursor-pointer whitespace-nowrap">
                  <input
                    type="checkbox"
                    checked={showDiscounts}
                    onChange={(e) => setShowDiscounts(e.target.checked)}
                    className="w-4 h-4 text-orange-600 border-gray-300 rounded-sm focus:ring-orange-500"
                  />
                  <span className="text-sm font-medium text-gray-700">Show Discounts</span>
                </label>
              )}

              <button
                onClick={handleExportExcel}
                disabled={exportingExcel || loading || !modalData?.period}
                className="flex items-center justify-center gap-2 px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed whitespace-nowrap"
              >
                {exportingExcel ? <Loader2 className="animate-spin" size={18} /> : <FileSpreadsheet size={18} />}
                <span>Excel</span>
              </button>
            </div>
          </div>
        </div>

        <div className="flex-1 overflow-auto">
          {loading ? (
            <div className="flex items-center justify-center h-64">
              <Loader2 className="animate-spin text-mila_orange" size={48} />
              <span className="ml-3 text-gray-600">Loading transactions...</span>
            </div>
          ) : error ? (
            <div className="flex items-center justify-center h-64">
              <div className="text-center">
                <p className="text-red-600 font-medium">{error}</p>
                <p className="text-sm text-gray-500 mt-2">Please try again later</p>
              </div>
            </div>
          ) : clientFilteredRows.length > 0 ? (
            <div className="overflow-x-auto">
              <table className="w-full border-collapse">
                <thead className="sticky top-0 bg-white shadow-xs">
                  <tr className="bg-gray-100 border-b-2 border-gray-300">
                    <th className="text-left p-3 text-sm font-semibold text-gray-700">Invoice</th>
                    <th className="text-left p-3 text-sm font-semibold text-gray-700">Branch</th>
                    <th className="text-left p-3 text-sm font-semibold text-gray-700">Chart Code</th>
                    <th className="text-left p-3 text-sm font-semibold text-gray-700">Chart Name</th>
                    <th className="text-left p-3 text-sm font-semibold text-gray-700">Memo</th>
                    <th className="text-right p-3 text-sm font-semibold text-gray-700">Amount</th>
                    <th className="text-right p-3 text-sm font-semibold text-gray-700">Discount</th>
                    <th className="text-right p-3 text-sm font-semibold text-gray-700">Fee</th>
                    <th className="text-right p-3 text-sm font-semibold text-gray-700">Settlement Amount</th>
                    <th className="text-left p-3 text-sm font-semibold text-gray-700">Settlement Date</th>
                    <th className="text-left p-3 text-sm font-semibold text-gray-700">Due Date</th>
                  </tr>
                </thead>
                <tbody>
                  {paginatedTransactions.map((txn, idx) => (
                    <tr
                      key={txn.id || idx}
                      className={`border-b hover:bg-orange-50 transition-colors ${idx % 2 === 0 ? "bg-white" : "bg-gray-50"}`}
                    >
                      <td className="p-3 text-sm text-gray-700">{txn.invoice_number || "-"}</td>
                      <td className="p-3 text-sm text-gray-700">{txn.filial_name || "-"}</td>
                      <td className="p-3 text-sm text-gray-700 font-mono text-xs">{txn.chart_code || "-"}</td>
                      <td className="p-3 text-sm text-gray-700">{txn.chart_name || "-"}</td>
                      <td className="p-3 text-sm text-gray-700 max-w-xs truncate" title={txn.memo}>{txn.memo || "-"}</td>
                      <td className="p-3 text-sm text-gray-700 text-right">{txn.amount ? USDollar.format(txn.amount) : "-"}</td>
                      <td className="p-3 text-sm text-gray-700 text-right">{txn.discount ? USDollar.format(txn.discount) : "-"}</td>
                      <td className="p-3 text-sm text-gray-700 text-right">{txn.fee ? USDollar.format(txn.fee) : "-"}</td>
                      <td className="p-3 text-sm text-gray-700 text-right font-semibold">{txn.settlement_amount ? USDollar.format(txn.settlement_amount) : "-"}</td>
                      <td className="p-3 text-sm text-gray-700">{formatDate(txn.settlement_date)}</td>
                      <td className="p-3 text-sm text-gray-700">{formatDate(txn.due_date)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ) : (
            <div className="flex items-center justify-center h-64 text-gray-500">
              {debouncedTerm ? `No results found for "${debouncedTerm}"` : "No transaction data available for this period"}
            </div>
          )}
        </div>

        {totalRowsUI > 0 && (
          <div className="flex items-center justify-between p-4 border-t bg-gray-50">
            <div className="text-sm text-gray-600">Page {currentPage} of {totalPages}</div>
            <div className="flex items-center gap-2">
              <button
                onClick={() => handlePageChange(currentPage - 1)}
                disabled={currentPage === 1 || loading}
                className="p-2 rounded-lg border border-gray-300 disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-100 transition-colors bg-white"
              >
                <ChevronLeft size={20} />
              </button>
              <span className="text-sm px-3 font-medium text-gray-700">
                {currentPage} / {totalPages}
              </span>
              <button
                onClick={() => handlePageChange(currentPage + 1)}
                disabled={currentPage === totalPages || loading}
                className="p-2 rounded-lg border border-gray-300 disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-100 transition-colors bg-white"
              >
                <ChevronRight size={20} />
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
